import React, { useState, useEffect, useCallback, useRef } from "react";
import Header from "../../components/Header";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SuccessNotification from "../../components/Notifications/SuccessNotification";
import ErrorNotification from "../../components/Notifications/ErrorNotification";
import LoginNotification from "../../components/Notifications/LoginNotification";
import {
  faEdit,
  faTrashAlt,
  faUserPlus,
  faEye,
  faSearch,
  faChalkboardUser,
  faEllipsisH,
  faCircleArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import Table from "../../components/Table";
import Section from "../../components/Section";
import SlideModal from "../../components/SlideModal";
import DeleteModal from "../../components/DeleteModal";
import apiClient from "../../axios";
import { Tooltip } from "react-tooltip";
import { useNavigate } from "react-router-dom";
import useEscapeKey from "../../hooks/useEscapeKey";

const TechnicianList = ({ handleLogout }) => {
  const [data, setData] = useState([]);
  //const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const itemsPerPage = 10;
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [isInfoVisible, setIsInfoVisible] = useState(false);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);
    
  useEffect(() => {
    const handleClickOutside = (event) => {
        const isClickInsideDropdown = dropdownRef.current?.contains(event.target);
        const isDropdownItem = event.target.closest(".dm-class");

        if (!isClickInsideDropdown && !isDropdownItem) {
        setOpenDropdown(false);
        }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
        document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEscapeKey(() => setIsModalOpen(false));

  const toggleModal = async (item) => {
    if (!isModalOpen) {
      try {
        const response = await apiClient.get(`/technicians/${item.idPc}`);
        const staff = {
          general: item,
          alternative: response.data,
        };
        setSelectedItem(staff);
        setIsModalOpen(true);
      } catch (error) {
        setIsErrorVisible(true);
        if (error.response && error.response.status === 401) {
          setIsInfoVisible(true);
        }
      } finally {
        setIsSuccessVisible(true);
      }
    } else {
      setIsModalOpen(false);
    }
  };

  const fetchAllData = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/technicians?page=${page}`);
      if (Array.isArray(response.data.results)) {
        setData(response.data.results);
        setTotalItems(response.data.total);
      } else {
        setData([response.data.results]);
        setTotalItems(1);
      }
    } catch (error) {
      setIsErrorVisible(true);
      if (error.response && error.response.status === 401) {
        setIsInfoVisible(true);
      }
    } finally {
      setIsSuccessVisible(true);
      setLoading(false);
    }
  }, [])

  const handleRefresh = () => {
    fetchAllData(currentPage);
  };

  const handleSearch = useCallback(
    async (page = 1) => {
      try {
        // Construir la URL con los query params
        const params = new URLSearchParams();
        if (searchTerm) params.append("search", searchTerm);

        const response = await apiClient.get(
          `/technicians?${params.toString()}&page=${page}`
        );

        if (Array.isArray(response.data.results)) {
          setData(response.data.results); // Si es un array, lo dejamos tal cual
        } else {
          setData([response.data.results]); // Si es un objeto, lo encapsulamos en un array
        }
        setTotalItems(response.data.total);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          setIsInfoVisible(true);
        }
      }
    },
    [searchTerm]
  );
  const handleFresh = async (code) => {
    //handleSearch(1);
    setCurrentPage(1);
    setSearchTerm(searchInput.trim());
  };

  useEffect(() => {
    if (searchTerm) {
      handleSearch(currentPage);
    } else {
      fetchAllData(currentPage);
    }

  }, [fetchAllData, handleSearch, searchTerm, currentPage]);

  const handleDeleteModal = (item) => {
    setSelectedDeleteItem(item);
    setDeleteItem(true);
  };
  const handleCloseDeleteModal = () => {
    setDeleteItem(false);
    setSelectedDeleteItem(null);
    handleRefresh();
  };

  const handleClick = () => {
    navigate(`/Management`);
  };

  const columns = [
    { title: "Id", key: "id" },
    { title: "Empresa", key: "empresa" },
    { title: "Técnico", key: "tecnico" },
    { title: "Acciones", key: "acciones" },
  ];

  const renderRow = (item, index) => (
    <>
      <td>{item.idPc}</td>
      <td>{item.empresaPc}</td>
      <td>{item.tecnicoPc}</td>
      <td>
        <div className="button-container">
          <div className="desktop-buttons">
            <Tooltip id="edit-tooltip" className="custom-tooltip" />
            <button
              data-tooltip-id="edit-tooltip"
              className="icon-button edit-button"
              data-tooltip-content="Editar"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/TechnicianForm/${item.idPc}`);
              }}
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <Tooltip id="delete-tooltip" className="custom-tooltip" />
            <button
              data-tooltip-id="delete-tooltip"
              data-tooltip-content="Eliminar"
              className="icon-button delete-button"
              //onClick={() => handleDelete(item.idPc)}
              onClick={() => handleDeleteModal(item)}
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </button>
            <Tooltip id="contract-tooltip" className="custom-tooltip" />
            <button
              className="icon-button company-button"
              onClick={() => toggleModal(item)}
              data-tooltip-id="contract-tooltip"
              data-tooltip-content="Detalle"
            >
              <FontAwesomeIcon icon={faEye} />
            </button>
          </div>

          <div className="mobile-dropdown">
            <div className="dropdown-container-contract">
              <button
                className="icon-button dropdown-button-contract"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdown(
                    openDropdown === item.idPc ? null : item.idPc
                  );
                }}
              >
                <FontAwesomeIcon icon={faEllipsisH} />
              </button>
              {openDropdown === item.idPc && (
                <div className="dropdown-menu-contract dm-class">
                  <button
                    className="dropdown-item-contract"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/TechnicianForm/${item.idPc}`);
                    }}
                  >
                    Editar
                  </button>
                  <button
                    className="dropdown-item-contract"
                    onClick={() => {
                      handleDeleteModal(item);
                      setOpenDropdown(false);
                    }}
                  >
                    Eliminar
                  </button>
                  <button
                    className="dropdown-item-contract"
                    onClick={() => toggleModal(item)}
                  >
                    Detalle
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </td>
    </>
  );

  return (
    <div className="home-container">
      <Header onLogout={handleLogout} title="Equipo Técnico" />
      <div className="home-content">
        <Section>
          <div className="button-return-container">
            <div className="contener-return">
              <FontAwesomeIcon
                className="basic-shortcut-icon"
                style={{ cursor: "pointer" }}
                icon={faCircleArrowLeft}
                onClick={handleClick}
              />
            </div>
            <div className="filter-form">
              <div className="basic-info-form-group">
                <input
                  type="text"
                  id="customer"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Equipo Técnico"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleFresh();
                    }
                  }}
                />
              </div>
              <button className="basic-custom-button" onClick={handleFresh}>
                <FontAwesomeIcon
                  icon={faSearch}
                  className="basic-shortcut-icon"
                />
                Buscar
              </button>
            </div>
          </div>

          <div className="button-add">
            <button
              className="basic-custom-button"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/TechnicianForm");
              }}
            >
              <FontAwesomeIcon
                className="basic-shortcut-icon"
                icon={faUserPlus}
              />
              Crear Nuevo Equipo Técnico
            </button>
          </div>
        </Section>
        <Table
          title="Lista de Equipos Técnicos"
          rows={data}
          columns={columns}
          icon={faChalkboardUser}
          renderRow={renderRow}
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onRefresh={handleRefresh}
          loading={loading}
          flagPagination={true}
        />
        <SuccessNotification
          message={"Se ha cargado correctamente"}
          isVisible={isSuccessVisible}
          onClose={() => setIsSuccessVisible(false)}
        />
        <ErrorNotification
          message="Ups! Ocurrio un Problema"
          isVisible={isErrorVisible}
          onClose={() => setIsErrorVisible(false)}
        />
        <LoginNotification
          message="Vuele a iniciar sesión"
          isVisible={isInfoVisible}
          onClose={() => setIsInfoVisible(false)}
          onLogout={handleLogout}
        />
        {selectedDeleteItem && (
          <DeleteModal
            isVisible={deleteItem}
            onClose={handleCloseDeleteModal}
            identificator={selectedDeleteItem.idPc}
            url={`/technicians`}
          />
        )}
        <SlideModal
          isOpen={isModalOpen}
          onClose={toggleModal}
          title="Detalles del Equipo Técnico"
        >
          <div className="section-container-form">
            <h4 className="section-title-form">ID</h4>
            <hr className="section-divider-form" />
            <div className="form-group-form">
              <label>{selectedItem?.general.idPc}</label>
            </div>

            <h4 className="section-title-form">EMPRESA</h4>
            <hr className="section-divider-form" />
            <div className="form-group-form">
              <label>{selectedItem?.general.empresaPc}</label>
            </div>

            <h4 className="section-title-form">TÉCNICO</h4>
            <hr className="section-divider-form" />
            <div className="form-group-form">
              <label>{selectedItem?.general.tecnicoPc}</label>
            </div>

            <h4 className="section-title-form">USUARIO</h4>
            <hr className="section-divider-form" />
            <div className="form-group-form">
              <label>{selectedItem?.general.usuario}</label>
            </div>

            <h4 className="section-title-form">ESTADO</h4>
            <hr className="section-divider-form" />
            <div className="form-group-form">
              <label>
                {selectedItem?.general.flagActive === 1 ? "Activo" : "Inactivo"}
              </label>
            </div>

            <h4 className="section-title-form">EMAIL</h4>
            <hr className="section-divider-form" />
            <div className="form-group-form">
              <label>{selectedItem?.alternative.email}</label>
            </div>
          </div>
        </SlideModal>
      </div>
    </div>
  );
};

export default TechnicianList;
